import { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import type { components } from "@dolfin/api-admin";
import { useAuth } from "@/auth/AuthContext";
import { api } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { Switch } from "@/components/ui/switch";
import { cn, dirtyRing } from "@/lib/utils";

// Client-level AR settings: they apply to every organisation under the client.

type Tone = components["schemas"]["InvoiceTone"];
const TONES: Tone[] = ["Friendly", "Direct", "Formal"];

// The part of a link format before its first token: what Meta fixes on a WhatsApp message's "Pay now" button.
const fixedPart = (format: string) => format.trim().split("{")[0];
export function ReceivablesSettingsSection() {
  const { activeClient, activeClientId } = useAuth();
  const isAdmin = activeClient?.role === "Owner" || activeClient?.role === "Admin";

  const [arEmailsEnabled, setArEmailsEnabled] = useState(true);
  const [integratedPayments, setIntegratedPayments] = useState(true);
  const [paymentLinkUrl, setPaymentLinkUrl] = useState("");
  const [whatsAppTone, setWhatsAppTone] = useState<Tone>("Friendly");
  const [supportedTokens, setSupportedTokens] = useState<string[]>([]);

  const [saved, setSaved] = useState({
    arEmailsEnabled: true,
    integratedPayments: true,
    paymentLinkUrl: "",
    whatsAppTone: "Friendly" as Tone,
  });
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!activeClientId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      const { data, error: apiError } = await api.GET("/v1/settings/ar", {});
      if (cancelled) return;
      if (apiError || !data) {
        setError("Failed to load receivables settings.");
        setLoading(false);
        return;
      }
      const link = data.defaultPaymentLinkUrl ?? "";
      setArEmailsEnabled(data.arEmailsEnabled);
      setIntegratedPayments(data.integratedPaymentsEnabled);
      setPaymentLinkUrl(link);
      setWhatsAppTone(data.whatsAppMessageTone);
      setSupportedTokens(data.supportedPaymentLinkTokens ?? []);
      setSaved({
        arEmailsEnabled: data.arEmailsEnabled,
        integratedPayments: data.integratedPaymentsEnabled,
        paymentLinkUrl: link,
        whatsAppTone: data.whatsAppMessageTone,
      });
      setLoaded(true);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [activeClientId]);

  const dirty =
    arEmailsEnabled !== saved.arEmailsEnabled ||
    integratedPayments !== saved.integratedPayments ||
    paymentLinkUrl.trim() !== saved.paymentLinkUrl ||
    whatsAppTone !== saved.whatsAppTone;

  // Saving makes new WhatsApp messages for Meta to review: on your own link, a new fixed part of it or a new tone.
  const needsWhatsAppReview =
    !integratedPayments &&
    paymentLinkUrl.trim().length > 0 &&
    (saved.integratedPayments ||
      fixedPart(paymentLinkUrl) !== fixedPart(saved.paymentLinkUrl) ||
      whatsAppTone !== saved.whatsAppTone);

  function reset() {
    setArEmailsEnabled(saved.arEmailsEnabled);
    setIntegratedPayments(saved.integratedPayments);
    setPaymentLinkUrl(saved.paymentLinkUrl);
    setWhatsAppTone(saved.whatsAppTone);
    setError(null);
  }

  async function save() {
    if (!dirty) return;
    // Mirrors the backend's mutual exclusion so the error shows inline.
    if (integratedPayments && paymentLinkUrl.trim().length > 0) {
      setError("Turn off integrated payments before setting your own payment link.");
      return;
    }
    setSaving(true);
    setError(null);
    const { data, error: apiError } = await api.PATCH("/v1/settings/ar", {
      body: {
        arEmailsEnabled,
        integratedPaymentsEnabled: integratedPayments,
        // "" clears any stale link when integrated payments are on.
        defaultPaymentLinkUrl: integratedPayments ? "" : paymentLinkUrl.trim(),
        whatsAppMessageTone: whatsAppTone,
      },
    });
    setSaving(false);
    if (apiError || !data) {
      setError((apiError as { detail?: string } | undefined)?.detail ?? "Failed to save changes.");
      return;
    }
    const link = data.defaultPaymentLinkUrl ?? "";
    setArEmailsEnabled(data.arEmailsEnabled);
    setIntegratedPayments(data.integratedPaymentsEnabled);
    setPaymentLinkUrl(link);
    setWhatsAppTone(data.whatsAppMessageTone);
    setSaved({
      arEmailsEnabled: data.arEmailsEnabled,
      integratedPayments: data.integratedPaymentsEnabled,
      paymentLinkUrl: link,
      whatsAppTone: data.whatsAppMessageTone,
    });
    toast.success("Receivables settings updated");
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center gap-2 py-12 text-sm text-muted-foreground">
          <Spinner className="size-4" /> Loading receivables settings…
        </CardContent>
      </Card>
    );
  }

  if (!loaded) {
    return (
      <Card>
        <CardContent className="py-12 text-sm text-destructive">
          {error ?? "Unable to load receivables settings."}
        </CardContent>
      </Card>
    );
  }

  const bringYourOwn = !integratedPayments;

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardHeader>
          <CardTitle>Invoicing</CardTitle>
          <CardDescription>
            How Organisations deliver invoices (Accounts Receivable) to their customers.
            {!isAdmin && " You need Admin or Owner role to edit these."}
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex flex-col gap-1">
              <Label htmlFor="ar-emails-enabled">Send AR emails</Label>
              <p className="text-xs text-muted-foreground">
                When on, sending an invoice emails it to the customer and schedules payment
                reminders. When off, invoices are still marked sent but Dolfin sends no email or
                reminders — your Organisations deliver invoices themselves.
              </p>
            </div>
            <Switch
              id="ar-emails-enabled"
              checked={arEmailsEnabled}
              disabled={!isAdmin || saving}
              onCheckedChange={setArEmailsEnabled}
              className={cn(arEmailsEnabled !== saved.arEmailsEnabled && dirtyRing)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Payments</CardTitle>
          <CardDescription>How customers pay the invoices your Organisations send.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-5">
          <div className="flex items-center justify-between gap-4">
            <div className="flex flex-col gap-1">
              <Label htmlFor="integrated-payments">Use Dolfin payments</Label>
              <p className="text-xs text-muted-foreground">
                When on, each invoice carries a Dolfin-generated payment link (card payments via
                Stripe). Turn off to bring your own payment link instead.
              </p>
            </div>
            <Switch
              id="integrated-payments"
              checked={integratedPayments}
              disabled={!isAdmin || saving}
              onCheckedChange={setIntegratedPayments}
              className={cn(integratedPayments !== saved.integratedPayments && dirtyRing)}
            />
          </div>

          {bringYourOwn && (
            <div className="flex flex-col gap-2 border-t pt-5">
              <Label htmlFor="payment-link-url">Your payment link</Label>
              <p className="text-xs text-muted-foreground">
                The URL customers are sent to pay. Use the tokens below and Dolfin fills them in per
                invoice, so your system knows which invoice is being paid.
              </p>
              <Input
                id="payment-link-url"
                type="url"
                inputMode="url"
                placeholder="https://pay.yourcompany.com/{organisationId}/{invoiceId}"
                value={paymentLinkUrl}
                disabled={!isAdmin || saving}
                onChange={(e) => setPaymentLinkUrl(e.target.value)}
                className={cn(paymentLinkUrl.trim() !== saved.paymentLinkUrl && dirtyRing)}
              />
              {supportedTokens.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5 pt-1">
                  <span className="text-xs text-muted-foreground">Available tokens:</span>
                  {supportedTokens.map((token) => (
                    <button
                      key={token}
                      type="button"
                      disabled={!isAdmin || saving}
                      onClick={() => setPaymentLinkUrl((prev) => prev + token)}
                      className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-foreground transition hover:bg-accent-blue/10 disabled:cursor-not-allowed disabled:opacity-60"
                      title={`Insert ${token}`}
                    >
                      {token}
                    </button>
                  ))}
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                Must be an absolute <code className="font-mono">https</code> URL. Leave empty to send
                invoices without a payment link. On WhatsApp, the "Pay now" button opens this link: the
                part before the first token is fixed when Meta approves the message.
              </p>
            </div>
          )}

          {bringYourOwn && (
            <div className="flex items-center justify-between gap-4 border-t pt-5">
              <div className="flex flex-col gap-1">
                <Label htmlFor="whatsapp-tone">WhatsApp tone</Label>
                <p className="text-xs text-muted-foreground">
                  The tone of invoices and reminders sent on WhatsApp with your payment link. One tone for
                  every Organisation, because Meta reviews each message.
                </p>
              </div>
              <Select value={whatsAppTone} onValueChange={(v) => setWhatsAppTone(v as Tone)} disabled={!isAdmin || saving}>
                <SelectTrigger id="whatsapp-tone" className={cn("w-36", whatsAppTone !== saved.whatsAppTone && dirtyRing)}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TONES.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </CardContent>
      </Card>

      {needsWhatsAppReview && (
        <div role="note" className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-sm">
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600 dark:text-amber-400" />
          <div className="flex flex-col gap-0.5">
            <span className="font-medium">WhatsApp messages need Meta's approval again</span>
            <span className="text-muted-foreground">
              Saving submits new WhatsApp invoice messages for this link and tone. Meta usually reviews them
              within a day. Until they are approved, invoices and reminders go by email instead of WhatsApp.
            </span>
          </div>
        </div>
      )}

      {error && <p className="text-sm text-destructive">{error}</p>}

      {isAdmin && (
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={reset} disabled={!dirty || saving}>
            Reset
          </Button>
          <Button onClick={save} disabled={!dirty || saving}>
            {saving && <Spinner className="size-4" />}
            Save changes
          </Button>
        </div>
      )}
    </div>
  );
}
